import uuid from '../../utils/uuid';
import { JSBridgeReturnType } from '../../typings/JSBridgeProtocol';
import { WeappBridgeMessage } from '../typings/WeappBridgeMessage';
import { WeappBridgeRequest } from '../typings/WeappBridgeRequest';
import { WeappBridgeResponse } from '../typings/WeappBridgeResponse';
import defaultWsEndpoint from '../../utils/defaultWsEndpoint';
import generateWssUrl from '../../utils/generateWssUrl';
import isValidKey from '../../utils/isValidKey';
import parseQuery from '../../utils/parseQuery';
import { RobustWebSocket } from './RobustWebSocket';
import { getSafelyWindow } from '../../utils/safelyWindow';

export interface HybridBridgeLJ {
  getStaticData: (functionName: string) => void;
  callAndBackfeed: (msg: string) => void;
  callAndListen: (msg: string) => void;
  invoke?<P, R>(method: string, args: P): Promise<R>;
  execute?<P, R>(method: string, args: P): Promise<R>;
  isReady: boolean;
}

interface RsSchemeRequest {
  id: string;
  actionUrl: string;
  type: 'callAndListen' | 'callAndBackfeed';
  command: 'rsSchemeRequest';
}
interface RsSchemeResponse<T = any> {
  id: string;
  scheme: string;
  result: T;
  errmsg: string;
  command: 'rsSchemeResponse';
}

export class HybridBridge implements HybridBridgeLJ {
  /**
   * @deprecated 兼容旧逻辑
   */
  private _rsWeappBridgeMethodAdapter: Record<string, Function> = {};

  private _ws: RobustWebSocket = new RobustWebSocket({
    url: '',
    robustOptions: {
      should1000Reconnect: true,
      ignoreConnectivityEvents: true,
    },
  });

  private _readyState: 'uninitialized' | 'connecting' | 'open' | 'close' =
    'close';

  get isReady(): boolean {
    return this._readyState === 'open';
  }

  constructor() {
    const $window = getSafelyWindow()
    if (
      $window?.__Realsee_WxMini_HybridBridge__ instanceof
      HybridBridge
    ) {
      return $window?.__Realsee_WxMini_HybridBridge__;
    }
    if ($window) {
      $window.__Realsee_WxMini_HybridBridge__ = this;
      this.initBuiltInHandlers();
    }
  }
  /**
   * 进行jsbridge的初始化，即打通ws通道
   * @param timeout 超时计时
   * @returns
   */
  initialize = async (timeout = 6000): Promise<JSBridgeReturnType<boolean>> => {
    const res: JSBridgeReturnType<boolean> = [false, null];

    const {
      wssid,
      wstsp,
      wssig,
      wsmnt = defaultWsEndpoint,
    } = parseQuery(globalThis.location.search);
    if (!(wssid && wstsp && wssig && wsmnt)) {
      res[1] = new Error('HybridBridge 参数缺失，初始化失败~');
      return res;
    }

    const url = generateWssUrl(
      '{{origin}}/{{endpoint}}/{{uuid}}/{{member}}/{{ak}}/{{ts}}/{{sign}}',
      {
        origin: wsmnt,
        endpoint: 'proxy',
        uuid: wssid,
        member: 'webview',
        ak: 'vr_live_miniapp',
        ts: wstsp,
        sign: wssig,
      },
    );

    try {
      const result = await this._open(url);
      if (result) {
        this.initBuiltInHandlers();
        res[0] = await this.waitReady(timeout);
      } else {
        res[1] = new Error('HybridBridge ws未连接，初始化失败~');
      }
    } catch (e) {
      res[1] = e as Error;
    }

    return res;
  };

  /**
   * 内建的处理函数
   */
  private initBuiltInHandlers = () => {
    this._registerLifecycleHandler();
    this._registerHeartbeatHandler();
    this._registerMessageHandler();
    this._registerBridgeRequestHandler();
    this._registerReadyHandler();
  };

  /**
   * 进行连接
   * @param url ws链接
   * @returns
   */
  private _open = (url: string) =>
    new Promise((resolve) => {
      const handleOpen = () => {
        this._receivedMessage(
          WeappBridgeMessage.ready,
          () => resolve(true),
          true,
        );
        this._ws.once('timeout', () => resolve(false));
        this.send(WeappBridgeMessage.isReady);
      };
      this._ws.once('open', handleOpen);
      this._ws.open(url);
    });

  /**
   * 注册生命周期回调
   */
  private _registerLifecycleHandler = () => {
    this._ws.on('connecting', () => {
      console.log('RsWeappBridge connecting');
      this._readyState = 'connecting';
    });

    this._ws.on('open', () => {
      console.log('RsWeappBridge open');
      this._readyState = 'open';
    });

    this._ws.on('close', () => {
      this._readyState = 'close';
    });

    this._ws.on('error', () => {
      console.log('RsWeappBridge error');
      this._readyState = 'close';
    });

    this._ws.on('timeout', () => {
      console.log('RsWeappBridge timeout');
      this._readyState = 'close';
    });
  };

  /**
   * 注册心跳的回调
   * @deprecated 兼容老的容器SDK
   */
  private _registerHeartbeatHandler = () => {
    // 收到ping时回复pong
    this._receivedMessage(WeappBridgeMessage.ping, () =>
      setTimeout(() => this._ws.send(WeappBridgeMessage.pong), 500),
    );
    // 收到pong时回复ping
    this._receivedMessage(WeappBridgeMessage.pong, () =>
      setTimeout(() => this._ws.send(WeappBridgeMessage.ping), 1000),
    );
  };

  /**
   * 当收到某消息时
   * @param targetMessage 目标消息
   * @param handler 回调
   * @param once 是否一次性处理
   */
  private _receivedMessage = (
    targetMessage: WeappBridgeMessage,
    handler: () => void,
    once?: boolean,
  ): void => {
    const msgHandler = (evnet: MessageEvent) => {
      if (evnet.data === targetMessage) {
        handler();
        if (once) {
          this._ws.off('message', msgHandler);
        }
      }
    };
    this._ws.on('message', msgHandler);
  };

  /**
   * 注册准备就绪的回调
   */
  private _registerReadyHandler = () => {
    // 收到就绪询问时回复已就绪
    this._receivedMessage(WeappBridgeMessage.isReady, () =>
      this._ws.send(WeappBridgeMessage.ready),
    );
  };

  /**
   * 发送消息
   */
  send = (data: string | ArrayBufferLike | Blob | ArrayBufferView): void => {
    this._ws.send(data);
  };

  /**
   * 等待响应
   * @param timeout 超时计时
   * @returns
   */
  waitReady = (timeout = 6000): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      this._receivedMessage(WeappBridgeMessage.ready, () => resolve(true));
      this.send(WeappBridgeMessage.isReady);
      setTimeout(() => reject('HybridBridge 初始化超时~'), timeout);
    });
  };

  private _parseMessage = <R = any>(event: MessageEvent) => {
    try {
      return JSON.parse(event.data) as R;
    } catch (e) {
      return event.data as R;
    }
  };

  /**
   * 注册消息投递回调
   * @deprecated 兼容老方法
   */
  private _registerMessageHandler = () => {
    this._ws.on('message', (event: MessageEvent) => {
      const message = this._parseMessage(event);
      if (typeof message === 'object') {
        const { id, command, data } = message;
        if (
          command === 'message' &&
          typeof this._rsWeappBridgeMethodAdapter.bindMessage === 'function'
        ) {
          this._rsWeappBridgeMethodAdapter.bindMessage({ id, data });
        }
      }
    });
  };

  /**
   * 注册bridge方法回调
   * @deprecated 兼容老方法
   */
  private _registerBridgeRequestHandler() {
    this._ws.on('message', async (event: MessageEvent) => {
      const request = this._parseMessage<WeappBridgeRequest>(event);
      if (typeof request === 'object') {
        const { id, method, args, command } = request;
        if (command === 'request') {
          if (method) {
            let result: any = null;
            let error: any = null;
            let _method: Function | null = null;
            if (
              typeof this._rsWeappBridgeMethodAdapter[method] === 'function'
            ) {
              // 优先读 _rsWeappBridgeMethodAdapter 上注册的方法
              _method = this._rsWeappBridgeMethodAdapter[method];
            } else if (globalThis && isValidKey(method, globalThis)) {
              // 再读 globalThis 下的方法
              _method = globalThis[method];
            }
            if (typeof _method === 'function') {
              try {
                result = await _method(args);
              } catch (e) {
                error = (e as Error).message || e;
              }
            } else {
              error = 'Method not implemented.';
            }
            this.send(
              JSON.stringify({
                id,
                method,
                result,
                error,
                command: 'response',
              } as WeappBridgeResponse),
            );
          }
        }
      }
    });
  }

  getStaticData = (callbackFunctionName: string): void => {
    this.callAndBackfeed(
      JSON.stringify({
        actionUrl: 'common/staticData',
        functionName: callbackFunctionName,
      }),
    );
  };

  /**
   * 处理 callAndBackfeed
   * @param actionUrl
   * @param backfeed
   */
  private _callAndBackfeed(actionUrl: string, backfeed: Function) {
    const reqId = uuid();
    const data = JSON.stringify({
      id: reqId,
      actionUrl,
      command: 'rsSchemeRequest',
      type: 'callAndBackfeed',
    } as RsSchemeRequest);
    let timer: NodeJS.Timeout | number | null = null;
    const callback = (event: MessageEvent) => {
      const message = this._parseMessage<RsSchemeResponse>(event);
      if (typeof message === 'object') {
        const { id, result, errmsg, command } = message;
        if (command === 'rsSchemeResponse' && id === reqId) {
          this._ws.off('message', callback);
          if (timer) clearTimeout(timer);
          if (errmsg) {
            console.warn(errmsg);
            backfeed(0);
          } else {
            backfeed(result);
          }
        }
      }
    };
    this._ws.on('message', callback);
    this.send(data);
    // 30s超时处理
    timer = globalThis.setTimeout(() => {
      console.warn('callAndBackfeed timeout');
      backfeed(0);
    }, 30000);
  }

  /**
   * 处理一次性执行的方法
   * @param msg JSON.stringify({ actionUrl: string, functionName: string })
   */
  callAndBackfeed = (msg: string) => {
    const { actionUrl = '', functionName = '' } = JSON.parse(msg);
    if (!actionUrl) throw new Error('callAndBackfeed actionUrl is empty!');
    if (!functionName)
      throw new Error('callAndBackfeed functionName is empty!');
    const callback = (result: any) => {
      if (typeof (globalThis as any)[functionName] === 'function') {
        (globalThis as any)[functionName](result);
      }
    };
    this._callAndBackfeed(actionUrl, callback);
  };

  /**
   * 处理 _callAndListen
   * @param actionUrl
   * @param fn
   */
  private _callAndListen(actionUrl: string, fn: Function) {
    const reqId = uuid();
    const data = JSON.stringify({
      id: reqId,
      actionUrl,
      command: 'rsSchemeRequest',
      type: 'callAndListen',
    } as RsSchemeRequest);
    const callback = (event: MessageEvent) => {
      const message = this._parseMessage<RsSchemeResponse>(event);
      if (typeof message === 'object') {
        const { id, result, errmsg, command } = message;
        if (command === 'rsSchemeResponse' && id === reqId) {
          if (errmsg) {
            console.warn(errmsg);
            fn(false);
          } else {
            fn(result);
          }
        }
      }
    };
    this._ws.on('message', callback);
    this.send(data);
  }

  /**
   * 处理监听
   * @param msg JSON.stringify({ actionUrl: string, functionName: string })
   */
  callAndListen = (msg: string) => {
    const { actionUrl, functionName } = JSON.parse(msg);

    if (!actionUrl) throw new Error('callAndListen actionUrl is empty!');
    if (!functionName) throw new Error('callAndListen functionName is empty!');

    const callback = (result: any) => {
      if (typeof (globalThis as any)[functionName] === 'function') {
        (globalThis as any)[functionName](result);
      }
    };
    try {
      this._callAndListen(actionUrl, callback);
    } catch (e) {
      console.warn(e);
      callback(false);
    }
  };

  /**
   * 执行bridge上的方法
   * @param method 方法名
   * @param args 传递的参数
   * @returns
   */
  async invoke<P, R>(method: string, args: P): Promise<R> {
    return new Promise((success, fail) => {
      const reqId = uuid();
      const data = JSON.stringify({
        id: reqId,
        method,
        args,
        command: 'request',
      });
      let timer: NodeJS.Timeout | number | null = null;
      const callback = (event: MessageEvent) => {
        const message = this._parseMessage<WeappBridgeResponse>(event);
        if (typeof message === 'object') {
          const { id, result, error, command } = message;
          if (
            command === 'response' &&
            id === reqId &&
            message.method === method
          ) {
            this._ws.off('message', callback);
            if (timer) clearTimeout(timer);
            if (error) {
              fail(error);
            } else {
              success(result as R);
            }
          }
        }
      };
      this._ws.on('message', callback);
      this.send(data);
      // 30s超时处理
      timer = setTimeout(() => fail('method invoke timeout'), 30000);
    });
  }

  /**
   * 执行
   * @param method
   * @param args
   * @returns
   */
  public execute = async <P = any, R = any>(
    method: string,
    args?: P,
  ): Promise<R> => await this._invokeBridgeMethod<R>(method, args);
  /**
   *
   * @param method
   * @param args
   * @returns
   * @deprecated 兼容老方法
   */
  private _invokeBridgeMethod = <R = any>(
    method: string,
    args?: any,
  ): Promise<R> =>
    new Promise<R>((success, fail) => {
      const id = uuid();
      const wsBridgeResponsehandler = (event: MessageEvent) => {
        const result = this._parseMessage<WeappBridgeResponse>(event);
        if (typeof result === 'object') {
          if (result.method === method && result.id === id) {
            this._ws.off('message', wsBridgeResponsehandler);
            result.error ? fail(result.error) : success(result.data);
          }
        }
      };
      this._ws.on('message', wsBridgeResponsehandler);
      this.send(JSON.stringify({ method, args, id }));
    });
}

export default HybridBridge;